import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Lock, Globe, MoreVertical, X, ImageOff, ImagePlus } from 'lucide-react';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import Header from '../../../shared/components/layout/Header';
import Layout from '../../components/layout/AppLayout';
import FormInput from '../../../shared/components/ui/FormInput';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import {
  getProjectImages,
  postProjectImagesBulkUpdate,
  uploadFilesToS3,
  postUnitProjectImages
} from '../../services/api/implementation/impl';
const IMAGE_TABS = [
  { id: 'Private', label: 'Private', Icon: Lock },
  { id: 'Public', label: 'Public', Icon: Globe }
];

const ProjectImages = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const { projectsForDropdown, isProjectsLoading } = useProjectsList();
  const { collapsed } = useSidebar();
  const { register, setValue, watch, formState: { errors } } = useForm();
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [activeTab, setActiveTab] = useState(IMAGE_TABS[0].id); // 'Private' | 'Public'
  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesError, setImagesError] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const menuRef = useRef(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadVisibility, setUploadVisibility] = useState('private');
  const [isUploading, setIsUploading] = useState(false);
  const uploadFileInputRef = useRef(null);

  const projectFieldValue = watch('project');

  // Map API shape (name, visibility) to UI shape (title, tag)
  const normalizedImages = images.map((img) => ({
    ...img,
    title: img.title ?? img.name,
    tag: (img.tag ?? img.visibility ?? 'public').toLowerCase()
  }));
  const filteredImages = normalizedImages.filter(
    (img) => img.tag === (activeTab === 'Private' ? 'private' : 'public')
  );

  const filteredIds = filteredImages.map((img) => img.id);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => new Set([...prev, ...filteredIds]));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Clear selection when switching tabs (only keep ids that exist in current tab)
  useEffect(() => {
    setSelectedIds((prev) => new Set([...prev].filter((id) => filteredIds.includes(id))));
  }, [activeTab, filteredIds.length]);

  const setImageTag = async (id, tag) => {
    try {
      await postProjectImagesBulkUpdate({
        images: [{ id, visibility: tag }]
      });
      setImages((prev) =>
        prev.map((img) => (img.id === id ? { ...img, tag, visibility: tag } : img))
      );
      setOpenMenuId(null);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success(`Image set to ${tag}`);
    } catch {
      toast.error('Failed to update image visibility');
    }
  };

  const bulkSetTag = async (tag) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      await postProjectImagesBulkUpdate({
        images: ids.map((id) => ({ id, visibility: tag }))
      });
      setImages((prev) =>
        prev.map((img) => (ids.includes(img.id) ? { ...img, tag, visibility: tag } : img))
      );
      setSelectedIds(new Set());
      toast.success(`${ids.length} image(s) set to ${tag}`);
    } catch {
      toast.error('Failed to update image visibility');
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (projectsForDropdown?.length > 0) {
      if (projectId) {
        setValue('project', parseInt(projectId, 10));
        setSelectedProjectId(parseInt(projectId, 10));
      } else if (!selectedProjectId) {
        const firstId = projectsForDropdown[0].value;
        setValue('project', firstId);
        setSelectedProjectId(firstId);
        navigate(`/builder/sales/project-images/${firstId}`, { replace: true });
      } else {
        setValue('project', selectedProjectId);
      }
    }
  }, [projectsForDropdown, projectId, selectedProjectId, setValue, navigate]);

  useEffect(() => {
    if (projectFieldValue && projectFieldValue !== selectedProjectId) {
      const id = parseInt(projectFieldValue, 10);
      setSelectedProjectId(id);
      navigate(`/builder/sales/project-images/${id}`);
    }
  }, [projectFieldValue, selectedProjectId, navigate]);

  const fetchProjectImages = useCallback((projectIdVal, isCancelled = () => false) => {
    if (!projectIdVal) {
      setImages([]);
      return Promise.resolve();
    }
    setImagesLoading(true);
    setImagesError(null);
    return getProjectImages(projectIdVal, 1, 500)
      .then((res) => {
        if (isCancelled()) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        const projectIdNum = parseInt(projectIdVal, 10);
        const forProject = list.filter(
          (item) =>
            item.project_id === projectIdNum &&
            item.block_id == null &&
            item.unit_id == null
        );
        setImages(
          forProject.map((item) => ({
            id: item.id,
            name: item.name,
            url: item.url,
            visibility: item.visibility,
            tag: (item.visibility || 'public').toLowerCase(),
            title: item.name,
            project_id: item.project_id
          }))
        );
      })
      .catch((err) => {
        if (isCancelled()) return;
        setImagesError(err?.response?.data?.message || err?.message || 'Failed to load project images');
        setImages([]);
        toast.error('Failed to load project images');
      })
      .finally(() => {
        if (!isCancelled()) setImagesLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedProjectId) {
      setImages([]);
      return;
    }
    let cancelled = false;
    fetchProjectImages(selectedProjectId, () => cancelled);
    return () => { cancelled = true; };
  }, [selectedProjectId, fetchProjectImages]);

  const handleUploadFilesChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    const valid = files.filter((f) => imageTypes.includes(f.type));
    if (valid.length < files.length) toast.error('Only image files (JPEG, PNG, GIF, WebP, BMP) are allowed.');
    setUploadFiles((prev) => [...prev, ...valid]);
    if (uploadFileInputRef.current) uploadFileInputRef.current.value = '';
  };

  const removeUploadFile = (index) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setUploadFiles([]);
    setUploadVisibility('private');
    if (uploadFileInputRef.current) uploadFileInputRef.current.value = '';
  };

  const handleUploadSubmit = async () => {
    if (!selectedProjectId || uploadFiles.length === 0) return;
    setIsUploading(true);
    try {
      const uploaded = await uploadFilesToS3(uploadFiles);
      if (uploaded?.length > 0) {
        await postUnitProjectImages({
          images: uploaded.map((f) => ({
            name: f.name,
            url: f.url,
            project_id: Number(selectedProjectId),
            visibility: uploadVisibility
          }))
        });
        toast.success(`${uploaded.length} image(s) added.`);
        closeUploadModal();
        await fetchProjectImages(selectedProjectId);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to upload images');
    } finally {
      setIsUploading(false);
    }
  };

  if (!projectId) {
    return (
      <>
        <Loader isVisible={isProjectsLoading} message="Loading projects..." />
        <Layout>
          <div
            className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
              ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
            `}
          >
            <Header data={{ name: 'Sales > Images' }} />
          </div>
          <div
            className={`flex items-center justify-between px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100 shadow-sm z-[10] transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'}`}
          >
            <div className="flex items-center gap-4 w-1/3">
              <FormInput
                width={100}
                label={''}
                register={register}
                register_key={'project'}
                isRequired={true}
                type={'dropdown'}
                options={[
                  { label: 'Select Project', value: '', disabled: true },
                  ...(projectsForDropdown || [])
                ]}
                errors={errors}
                hideAstrik={true}
                mb
              />
            </div>
          </div>
          <div className="mt-[9rem] h-[calc(100vh-9.5rem)] overflow-auto bg-gray-100 flex flex-col items-center justify-center">
            <h2 className="text-lg font-bold text-gray-800 mb-1">Select a project</h2>
            <p className="text-gray-600 text-sm">Choose a project to view project images.</p>
          </div>
        </Layout>
      </>
    );
  }

  return (
    <Layout>
      <div
        className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
          ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
        `}
      >
        <Header data={{ name: 'Sales > Images' }} />
      </div>
      <div
        className={`flex items-center justify-between px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100 shadow-sm z-[10] transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'}`}
      >
        <div className="flex flex-row items-center gap-6 flex-nowrap">
          <FormInput
            width={108}
            label={''}
            register={register}
            register_key={'project'}
            isRequired={true}
            type={'dropdown'}
            options={[
              { label: 'Select Project', value: '', disabled: true },
              ...(projectsForDropdown || [])
            ]}
            errors={errors}
            hideAstrik={true}
            mb
          />
          <div className="flex items-center bg-white p-1 rounded-lg border border-gray-100 shadow-sm">
            {IMAGE_TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  activeTab === id
                    ? 'bg-gray-100 text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon size={14} className="mr-1.5 shrink-0" />
                {label}
              </button>
            ))}
          </div>
          {selectedProjectId && (
            <button
              type="button"
              onClick={() => navigate(`/builder/sales/block-units-images/${selectedProjectId}`)}
              className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50 shrink-0"
            >
              View Block images
            </button>
          )}
        </div>
      </div>
      <div className="mt-[9rem] min-h-[calc(100vh-9.5rem)] overflow-auto bg-gray-100 px-6 pb-6 pt-4 flex flex-col">
        {imagesLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader isVisible message="Loading project images..." />
          </div>
        )}
        {!imagesLoading && imagesError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {imagesError}
          </div>
        )}
        {!imagesLoading && !imagesError && (
        <div className="flex flex-col flex-1 min-h-0">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-stone-800">
              Images {activeTab === 'Private' ? '(Private)' : '(Public)'}
            </h2>
            {selectedProjectId && (
              <button
                type="button"
                onClick={() => setUploadModalOpen(true)}
                className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
              >
                <ImagePlus size={18} />
                Add images
              </button>
            )}
            {filteredImages.length > 0 && (
              <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-stone-600">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAll}
                  className="rounded border-stone-300 text-primary focus:ring-primary"
                />
                Select all
              </label>
            )}
          </div>
          <p className="text-sm text-stone-500">
            {filteredImages.length} image{filteredImages.length !== 1 ? 's' : ''}
          </p>
        </div>

        {selectedIds.size > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 bg-white px-4 py-3 shadow-sm">
            <span className="text-sm font-medium text-stone-700">
              {selectedIds.size} selected
            </span>
            {activeTab === 'Private' && (
              <button
                type="button"
                onClick={() => bulkSetTag('public')}
                className="flex items-center gap-1.5 rounded-md border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
              >
                <Globe size={14} />
                Make Public
              </button>
            )}
            {activeTab === 'Public' && (
              <button
                type="button"
                onClick={() => bulkSetTag('private')}
                className="flex items-center gap-1.5 rounded-md border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
              >
                <Lock size={14} />
                Make Private
              </button>
            )}
            <button
              type="button"
              onClick={clearSelection}
              className="flex items-center gap-1.5 rounded-md border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-stone-600 hover:bg-stone-50 ml-auto"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        )}

        {filteredImages.length === 0 ? (
          <div className="flex flex-1 min-h-0 flex-col items-center justify-center rounded-xl border border-stone-200 bg-white py-16 px-6 shadow-sm">
            <ImageOff className="mb-4 h-14 w-14 text-stone-300" strokeWidth={1.25} aria-hidden />
            <p className="text-center text-base font-medium text-stone-600">
              No images available for this project yet.
            </p>
            <p className="mt-1 text-center text-sm text-stone-500">
              {activeTab === 'Private'
                ? 'Upload or add images to see them listed here as private.'
                : 'Upload or add images and set them to public to see them here.'}
            </p>
          </div>
        ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {filteredImages.map((img) => (
            <div
              key={img.id}
              className={`rounded-lg border bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow relative group ${
                selectedIds.has(img.id) ? 'border-primary ring-2 ring-primary/30' : 'border-stone-200'
              }`}
              ref={openMenuId === img.id ? menuRef : null}
            >
              <div className="aspect-square bg-stone-100 relative">
                <img
                  src={img.url}
                  alt={img.title ?? img.name ?? 'Project image'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/300x300?text=Image';
                  }}
                />
                <div className="absolute top-2 right-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === img.id ? null : img.id);
                    }}
                    className="p-1.5 rounded-md bg-white/90 hover:bg-white shadow-sm border border-stone-200"
                    aria-label="Edit image"
                  >
                    <MoreVertical size={16} className="text-stone-600" />
                  </button>
                  {openMenuId === img.id && (
                    <div
                      className="absolute right-0 top-full mt-1 py-1 w-44 bg-white rounded-lg border border-stone-200 shadow-lg z-20"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {img.tag === 'private' && (
                        <button
                          type="button"
                          onClick={() => setImageTag(img.id, 'public')}
                          className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm text-stone-700 hover:bg-stone-50"
                        >
                          <Globe size={14} />
                          Make Public
                        </button>
                      )}
                      {img.tag === 'public' && (
                        <button
                          type="button"
                          onClick={() => setImageTag(img.id, 'private')}
                          className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm text-stone-700 hover:bg-stone-50"
                        >
                        <Lock size={14} />
                        Make Private
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 border-t border-stone-100">
                <label className="flex items-center shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(img.id)}
                    onChange={() => toggleSelect(img.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-stone-300 text-primary focus:ring-primary w-4 h-4"
                  />
                </label>
                <p className="text-sm font-medium text-stone-700 truncate min-w-0" title={img.title ?? img.name}>
                  {img.title ?? img.name}
                </p>
              </div>
            </div>
          ))}
        </div>
        )}
        </div>
        )}
      </div>

      {uploadModalOpen && (
        <Modal
          isDirty={uploadFiles.length > 0}
          confirmOnClose={true}
          name="Add project images"
          width={45}
          onClose={closeUploadModal}
        >
          <div className="flex flex-col gap-4 p-2">
            <div>
              <label className="mb-1 block font-medium text-stone-700">Visibility</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="uploadVisibility"
                    checked={uploadVisibility === 'private'}
                    onChange={() => setUploadVisibility('private')}
                    className="rounded border-stone-300 text-primary focus:ring-primary"
                  />
                  <Lock size={16} className="text-stone-500" />
                  Private
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="uploadVisibility"
                    checked={uploadVisibility === 'public'}
                    onChange={() => setUploadVisibility('public')}
                    className="rounded border-stone-300 text-primary focus:ring-primary"
                  />
                  <Globe size={16} className="text-stone-500" />
                  Public
                </label>
              </div>
            </div>
            <div>
              <label className="mb-1 block font-medium text-stone-700">Images</label>
              <input
                ref={uploadFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
                multiple
                className="hidden"
                onChange={handleUploadFilesChange}
              />
              <button
                type="button"
                onClick={() => uploadFileInputRef.current?.click()}
                className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
              >
                <ImagePlus size={18} />
                Select images
              </button>
              {uploadFiles.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {uploadFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-2 py-1.5 text-sm"
                    >
                      <span className="truncate max-w-[180px]" title={file.name}>{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeUploadFile(index)}
                        className="p-0.5 rounded hover:bg-stone-200 text-stone-500"
                        aria-label="Remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
              <button
                type="button"
                onClick={closeUploadModal}
                className="rounded-lg border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUploadSubmit}
                disabled={uploadFiles.length === 0 || isUploading}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default ProjectImages;
